import { type AgreementDocumentFormat, AgreementDocumentFormatValues, ExternalLinkType } from '@inpart-io/padme-iap';
import { computed, type ComputedRef, onBeforeMount, type Ref, ref } from 'vue';

import { STRING_REGEX } from '@/constants/validation-regex.constants';

export function useAttachmentFormHelper(noneAllow?: boolean): {
    file: Ref<File>;
    linkLabel: Ref<string>;
    linkType: Ref<string>;
    linkUrl: Ref<string>;
    format: Ref<AgreementDocumentFormat>;
    isValid: ComputedRef<boolean>;
    isFileFormat: ComputedRef<boolean>;
    isLinkFormat: ComputedRef<boolean>;
    updateFile: (file: File) => void;
    updateType: (type: string) => void;
    updateLink: (link: string) => void;
    updateLabel: (label: string) => void;
    updateFormat: (format: AgreementDocumentFormat) => void;
} {
    const file = ref<File>(null);
    const linkLabel = ref<string>(null);
    const linkType = ref(ExternalLinkType.DEFAULT);
    const linkUrl = ref<string>(null);
    const format = ref<AgreementDocumentFormat>(null);

    const identifyLink = (value: string): string => {
        if (STRING_REGEX.YOUTUBE.test(value)) {
            linkType.value = ExternalLinkType.YOUTUBE;
        } else if (STRING_REGEX.GOOGLE_DRIVE.test(value)) {
            linkType.value = ExternalLinkType.GOOGLE_DRIVE;
        } else if (STRING_REGEX.SHAREPOINT.test(value)) {
            linkType.value = ExternalLinkType.SHAREPOINT;
        } else {
            linkType.value = ExternalLinkType.DEFAULT;
        }
        return linkType.value;
    };

    const updateFile = (newFile: File): void => {
        file.value = newFile;
    };

    const updateType = (newType: ExternalLinkType): void => {
        linkType.value = newType;
    };

    const updateLink = (newLink: string): void => {
        linkUrl.value = newLink;
        identifyLink(newLink);
    };

    const updateLabel = (newLabel: string): void => {
        linkLabel.value = newLabel;
    };

    const updateFormat = (newValue: string): void => {
        format.value = newValue as AgreementDocumentFormat;
        if (newValue === AgreementDocumentFormatValues.LINK) {
            file.value = null;
        } else if (newValue === AgreementDocumentFormatValues.FILE) {
            linkUrl.value = null;
        } else {
            file.value = null;
            linkLabel.value = null;
        }
    };

    const isValid = computed<boolean>(() => {
        return format.value !== AgreementDocumentFormatValues.NONE && (linkUrl.value !== null || file.value !== null);
    });

    const isFileFormat = computed<boolean>(() => {
        return format.value === AgreementDocumentFormatValues.FILE;
    });

    const isLinkFormat = computed<boolean>(() => {
        return format.value === AgreementDocumentFormatValues.LINK;
    });

    onBeforeMount(() => {
        if (noneAllow) {
            format.value = AgreementDocumentFormatValues.NONE;
        } else {
            format.value = AgreementDocumentFormatValues.FILE;
        }
    });

    return { updateFile, updateType, updateLink, updateLabel, updateFormat, file, linkLabel, linkType, linkUrl, format, isValid, isFileFormat, isLinkFormat };
}
